import { FC, useMemo } from 'react';
import {
  Avatar,
  Box,
  Stack,
  Text,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { Paginate } from '../../../appPaginate';
import { createColumnHelper } from '@tanstack/react-table';
import { NewDataGrid } from '../../../NewGridTable/NewDataGrid';
import { MetricModalLoading } from './DefaultDetailsModal/MetricModalLoading';

export interface WellbeingMemberItem {
  id?: string | number;
  name: string | null;
  email: string;
  role?: string | null;
}

interface WellbeingMemberData {
  data: WellbeingMemberItem[];
  currentPage?: number;
  totalPages?: number;
}

interface WellbeingMemberDetailsModalProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  wellbeingMemberData?: WellbeingMemberData;
  heading: string;
  subHeading: string;
  isFetching: boolean;
  handlePageClick: (page: number) => void;
}

const WellbeingMemberDetailsModal: FC<WellbeingMemberDetailsModalProps> = ({
  openDrawer,
  handleDrawerClose,
  wellbeingMemberData,
  heading,
  subHeading,
  isFetching,
  handlePageClick,
}) => {
  const rows: WellbeingMemberItem[] = useMemo(
    () => wellbeingMemberData?.data || [],
    [wellbeingMemberData?.data]
  );

  const columnHelper = createColumnHelper<WellbeingMemberItem>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        enableSorting: false,
        size: 240,
        cell: (info) => {
          const name = info.getValue();
          const email = info.row.original?.email;

          return (
            <Flex align="center">
              <Avatar size="xs" name={name || undefined} />
              <Link to={email ? `/contributors/${email}` : '#'}>
                <Text
                  ml={2}
                  fontFamily="heading"
                  fontSize="sm"
                  color="primary.500"
                  fontWeight="medium"
                  textDecoration="underline"
                  noOfLines={1}
                >
                  {name || '-'}
                </Text>
              </Link>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        enableSorting: false,
        size: 280,
        cell: (info) => {
          const email = info.getValue();
          if (!email) {
            return '-';
          }

          return (
            <Link to={`/contributors/${email}`}>
              <Text
                fontFamily="heading"
                fontSize="sm"
                color="text.secondary"
                fontWeight="medium"
                textDecoration="underline"
                noOfLines={1}
              >
                {email}
              </Text>
            </Link>
          );
        },
      }),
    ],
    [columnHelper]
  );

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent minW="4xl" maxW="4xl">
        <ModalHeader pb={2}>
          <Flex justify="space-between" align="flex-start" gap={4}>
            <Stack spacing={1}>
              <Text
                fontFamily="heading"
                fontSize="lg"
                fontWeight="bold"
                color="text.primary"
              >
                {heading}
              </Text>
              <Text
                fontFamily="heading"
                fontSize="sm"
                color="text.secondary"
                fontWeight="medium"
              >
                {subHeading}
              </Text>
            </Stack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {isFetching && !wellbeingMemberData?.data ? (
              <MetricModalLoading />
            ) : rows && rows.length > 0 ? (
              <>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  overflow="hidden"
                  w="100%"
                >
                  <NewDataGrid
                    data={rows}
                    columns={columns}
                    showLoader={false}
                    skeletonLoader={false}
                    sticky="none"
                    useMaxH
                    showGridLines
                  />
                </Box>

                {wellbeingMemberData?.data?.length &&
                  wellbeingMemberData.totalPages &&
                  wellbeingMemberData.totalPages > 1 && (
                    <Flex justify="center" mt={2}>
                      <Paginate
                        currentPage={(wellbeingMemberData.currentPage ?? 0) + 1}
                        totalPageCount={wellbeingMemberData.totalPages ?? 0}
                        pageSize={20}
                        onPageChange={(page) => handlePageClick(page as number)}
                      />
                    </Flex>
                  )}
              </>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default WellbeingMemberDetailsModal;
